import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { RotateCcw, Trash2, Loader2, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { hapticTap, hapticSuccess } from "@/lib/haptics";
import BottomSheet from "./BottomSheet";
import { CountUp, PeriodToggle, CategoryBar } from "./ExpensesTile";
import { EXPENSE_CATS, EXPENSE_BY, PERIODS, fmtEur } from "./constants";

const QUICK = [1, 5, 10, 50];
const today = () => new Date().toISOString().slice(0, 10);

export function ExpenseAddSheet({ open, dogId, dogName, initialCategory, onClose, onSaved, onLocked }) {
  const [cat, setCat] = useState(initialCategory || "cibo");
  const [amount, setAmount] = useState(0);
  const [typed, setTyped] = useState("");
  const [date, setDate] = useState(today());
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [bump, setBump] = useState(0);

  useEffect(() => {
    if (!open) return;
    setCat(initialCategory || "cibo"); setAmount(0); setTyped(""); setDate(today()); setNote("");
  }, [open, initialCategory]);

  const add = (n) => { hapticTap(); setAmount((a) => Math.round((a + n) * 100) / 100); setTyped(""); setBump((b) => b + 1); };
  const onType = (v) => {
    const clean = v.replace(",", ".").replace(/[^0-9.]/g, "");
    setTyped(clean);
    setAmount(Number(clean) || 0);
  };
  const c = EXPENSE_BY[cat];

  const save = async () => {
    if (amount <= 0) return toast.error("Inserisci un importo");
    setSaving(true);
    try {
      await api.post("/expenses", { dog_id: dogId, category: cat, amount_eur: amount, date, note: note || null }, { skipGlobalPaywall: true });
      hapticSuccess();
      toast.success(`${fmtEur(amount)} · ${c.label}`);
      onSaved();
    } catch (e) {
      if (e.response?.status === 402) onLocked();
      else toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  return (
    <BottomSheet open={open} onClose={onClose} kicker={`Spesa per ${dogName}`} title="Nuova spesa" testid="expense-add-sheet"
      footer={
        <motion.button whileTap={{ scale: 0.97 }} onClick={save} disabled={saving || amount <= 0} data-testid="expense-save"
          className="w-full py-3.5 rounded-full text-white font-semibold text-[15px] disabled:opacity-50 inline-flex items-center justify-center gap-2"
          style={{ background: `linear-gradient(135deg, ${c.color} 0%, #F4B83C 140%)` }}>
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Aggiungi {fmtEur(amount)} a {c.label}
        </motion.button>
      }>
      <div className="grid grid-cols-4 gap-2">
        {EXPENSE_CATS.map((x) => {
          const active = x.id === cat;
          return (
            <motion.button key={x.id} whileTap={{ scale: 0.92 }} animate={{ scale: active ? 1.04 : 1 }}
              onClick={() => { hapticTap(); setCat(x.id); }} data-testid={`expense-category-${x.id}`}
              className="aspect-square rounded-2xl flex flex-col items-center justify-center gap-1.5 border p-1"
              style={{ background: active ? x.color : "white", borderColor: active ? x.color : "#F0EBE3" }}>
              <x.icon className="h-5 w-5" style={{ color: active ? "white" : x.color }} />
              <span className={`text-[10px] font-semibold leading-tight text-center ${active ? "text-white" : "text-[#6B6B6B]"}`}>{x.label}</span>
            </motion.button>
          );
        })}
      </div>

      <div className="mt-5 rounded-[24px] bg-white border border-[#F0EBE3] p-5 text-center">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#9A9A9A] font-semibold">Importo</div>
        <motion.div key={bump} initial={{ scale: 1.08 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 500, damping: 18 }}>
          <CountUp value={amount} testid="expense-amount" className="font-display text-5xl tabular-nums text-[#1A1A1A]" />
        </motion.div>
        <div className="mt-4 grid grid-cols-4 gap-2">
          {QUICK.map((n) => (
            <motion.button key={n} whileTap={{ scale: 0.9 }} onClick={() => add(n)} data-testid={`expense-quick-add-${n}`}
              className="py-2.5 rounded-2xl text-[14px] font-bold border"
              style={{ background: `${c.color}12`, borderColor: `${c.color}40`, color: c.color }}>
              +{n}
            </motion.button>
          ))}
        </div>
        <div className="mt-3 flex gap-2">
          <input inputMode="decimal" value={typed} onChange={(e) => onType(e.target.value)} placeholder="Importo esatto (es. 23,90)"
            data-testid="expense-amount-input"
            className="flex-1 px-4 py-2.5 rounded-2xl bg-[#FBF7F1] border border-[#F0EBE3] outline-none text-[14px] text-[#1A1A1A]" />
          <button onClick={() => { setAmount(0); setTyped(""); }} aria-label="Azzera" data-testid="expense-reset"
            className="h-11 w-11 rounded-2xl bg-[#F4EFE6] flex items-center justify-center text-[#6B6B6B]">
            <RotateCcw className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-[1fr_1.4fr] gap-2">
        <label className="relative">
          <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9A9A9A] pointer-events-none" />
          <input type="date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} data-testid="expense-date"
            className="w-full pl-9 pr-2 py-2.5 rounded-2xl bg-white border border-[#F0EBE3] outline-none text-[13px] text-[#1A1A1A]" />
        </label>
        <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={140} placeholder="Nota (es. Crocchette 12kg)"
          data-testid="expense-note"
          className="px-4 py-2.5 rounded-2xl bg-white border border-[#F0EBE3] outline-none text-[13px] text-[#1A1A1A]" />
      </div>
    </BottomSheet>
  );
}

function groupByDate(items) {
  const g = {};
  for (const it of items) (g[it.date] = g[it.date] || []).push(it);
  return Object.entries(g);
}

export function ExpenseListSheet({ open, dogId, dogName, period: initialPeriod, onClose, onChanged }) {
  const [period, setPeriod] = useState(initialPeriod);
  const [data, setData] = useState(null);

  useEffect(() => { if (open) setPeriod(initialPeriod); }, [open, initialPeriod]);
  useEffect(() => {
    if (!open || !dogId) return;
    api.get("/expenses", { params: { dog_id: dogId, period } }).then((r) => setData(r.data)).catch(() => setData(null));
  }, [open, dogId, period]);

  const groups = useMemo(() => groupByDate(data?.items || []), [data]);
  const cats = EXPENSE_CATS.filter((c) => data?.by_category?.[c.id] > 0)
    .sort((a, b) => data.by_category[b.id] - data.by_category[a.id]);

  const del = async (id) => {
    if (!window.confirm("Eliminare questa spesa?")) return;
    await api.delete(`/expenses/${id}`);
    setData((d) => {
      const it = d.items.find((x) => x.id === id);
      const by = { ...d.by_category, [it.category]: Math.round((d.by_category[it.category] - it.amount_eur) * 100) / 100 };
      return { ...d, items: d.items.filter((x) => x.id !== id), total: Math.round((d.total - it.amount_eur) * 100) / 100, by_category: by, count: d.count - 1 };
    });
    toast.success("Spesa eliminata");
    onChanged();
  };

  return (
    <BottomSheet open={open} onClose={onClose} kicker={`Spese di ${dogName}`} title="Tutte le spese" testid="expense-list-sheet">
      <div className="flex items-center justify-between gap-2">
        <PeriodToggle period={period} onChange={setPeriod} idPrefix="expense-list-period" />
        <span className="text-[12px] text-[#9A9A9A]">{PERIODS.find((p) => p.id === period)?.short}</span>
      </div>
      <div className="mt-4 rounded-[24px] bg-white border border-[#F0EBE3] p-5">
        <CountUp value={data?.total || 0} testid="expense-list-total" className="font-display text-4xl tabular-nums text-[#1A1A1A]" />
        <div className="mt-3"><CategoryBar byCategory={data?.by_category} total={data?.total} height={12} /></div>
        <div className="mt-4 space-y-2.5">
          {cats.map((c) => (
            <div key={c.id} className="flex items-center gap-3" data-testid={`expense-breakdown-${c.id}`}>
              <span className="h-8 w-8 rounded-xl flex items-center justify-center" style={{ background: `${c.color}18` }}>
                <c.icon className="h-4 w-4" style={{ color: c.color }} />
              </span>
              <span className="flex-1 text-[13px] text-[#1A1A1A]">{c.label}</span>
              <span className="text-[12px] text-[#9A9A9A] tabular-nums">{Math.round((data.by_category[c.id] / (data.total || 1)) * 100)}%</span>
              <span className="w-20 text-right text-[13px] font-semibold tabular-nums">{fmtEur(data.by_category[c.id])}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5 space-y-4" data-testid="expense-list">
        {groups.length === 0 && <div className="text-center text-[13px] text-[#9A9A9A] py-8">Nessuna spesa in questo periodo</div>}
        {groups.map(([date, items]) => (
          <div key={date}>
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#9A9A9A] font-semibold mb-2">
              {new Date(date).toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "long", year: "numeric" })}
            </div>
            <div className="space-y-2">
              <AnimatePresence initial={false}>
                {items.map((it) => {
                  const c = EXPENSE_BY[it.category] || EXPENSE_BY.extra;
                  return (
                    <motion.div key={it.id} layout initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, x: -40 }}
                      data-testid={`expense-row-${it.id}`}
                      className="flex items-center gap-3 p-3 rounded-2xl bg-white border border-[#F0EBE3]">
                      <span className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${c.color}18` }}>
                        <c.icon className="h-5 w-5" style={{ color: c.color }} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-[14px] font-medium text-[#1A1A1A] truncate">{it.note || c.label}</div>
                        <div className="text-[11px] text-[#9A9A9A]">{c.label}{it.source_doc_id ? " · da fattura AI" : ""}</div>
                      </div>
                      <span className="text-[15px] font-semibold tabular-nums">{fmtEur(it.amount_eur)}</span>
                      <button onClick={() => del(it.id)} aria-label="Elimina" data-testid={`expense-delete-${it.id}`}
                        className="h-8 w-8 rounded-full flex items-center justify-center text-[#9A9A9A] hover:text-[#E94545]">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          </div>
        ))}
      </div>
    </BottomSheet>
  );
}
